import { useCallback, useEffect, useRef } from 'react'

const SOUND_URL = '/sounds/generation-complete.mp3'
const TITLE_FLASH_TEXT = 'Your content is ready!'
const TITLE_FLASH_INTERVAL_MS = 1000

let audioCtx: AudioContext | null = null
let audioBuffer: AudioBuffer | null = null
let unlockListenerAttached = false

function getAudioContext(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext()
  return audioCtx
}

async function preloadAudio(): Promise<void> {
  if (audioBuffer) return
  try {
    const ctx = getAudioContext()
    if (ctx.state === 'suspended') await ctx.resume()
    const response = await fetch(SOUND_URL)
    const arrayBuffer = await response.arrayBuffer()
    audioBuffer = await ctx.decodeAudioData(arrayBuffer)
  } catch {}
}

function attachUnlockListener(): void {
  if (unlockListenerAttached || typeof window === 'undefined') return
  unlockListenerAttached = true
  const unlock = () => {
    void preloadAudio()
    window.removeEventListener('click', unlock, true)
    window.removeEventListener('keydown', unlock, true)
  }
  window.addEventListener('click', unlock, true)
  window.addEventListener('keydown', unlock, true)
}

async function playSound(): Promise<void> {
  try {
    const ctx = getAudioContext()
    if (ctx.state === 'suspended') await ctx.resume()
    if (audioBuffer) {
      const gain = ctx.createGain()
      gain.gain.value = 0.3
      gain.connect(ctx.destination)
      const source = ctx.createBufferSource()
      source.buffer = audioBuffer
      source.connect(gain)
      source.start()
    } else {
      const audio = new Audio(SOUND_URL)
      audio.volume = 0.3
      await audio.play()
    }
  } catch {}
}

export const useGenerationCompleteNotification = () => {
  const flashIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const originalTitleRef = useRef<string | null>(null)
  const flashStateRef = useRef(false)

  useEffect(() => {
    attachUnlockListener()
  }, [])

  const stopFlashing = useCallback(() => {
    if (flashIntervalRef.current !== null) {
      clearInterval(flashIntervalRef.current)
      flashIntervalRef.current = null
    }
    if (originalTitleRef.current !== null) {
      document.title = originalTitleRef.current
      originalTitleRef.current = null
    }
    flashStateRef.current = false
  }, [])

  const notify = useCallback(() => {
    void playSound()

    stopFlashing()

    originalTitleRef.current = document.title
    document.title = TITLE_FLASH_TEXT
    flashStateRef.current = true

    flashIntervalRef.current = setInterval(() => {
      flashStateRef.current = !flashStateRef.current
      document.title = flashStateRef.current ? TITLE_FLASH_TEXT : originalTitleRef.current ?? document.title
    }, TITLE_FLASH_INTERVAL_MS)

    const handleReturn = () => {
      if (document.visibilityState === 'visible') {
        stopFlashing()
        document.removeEventListener('visibilitychange', handleReturn)
        window.removeEventListener('focus', handleReturn)
      }
    }

    document.addEventListener('visibilitychange', handleReturn)
    window.addEventListener('focus', handleReturn)
  }, [stopFlashing])

  return { notify }
}
